import { Button, Col, Form, FormInstance, Row, Select } from 'antd';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';

import { Feature } from '../../../shared/models/features';
import { Method } from '../../../shared/models/method';
import { translationNamespace } from '../../constants/translation-resources';
import { Methods } from '../../../shared/constants/method';
import { RangePicker } from '../../../shared/components/date-picker/date-picker';

enum PoliciesTableFilter {
  NAME = 'name',
  METHOD = 'method',
  PATH = 'path',
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
}

export interface PoliciesTableFilters {
  [PoliciesTableFilter.NAME]: string[];
  [PoliciesTableFilter.METHOD]: Method[];
  [PoliciesTableFilter.PATH]: string[];
  [PoliciesTableFilter.CREATED_AT]: [Date?, Date?];
  [PoliciesTableFilter.UPDATED_AT]: [Date?, Date?];
}

interface PoliciesTableFiltersFormProps {
  onApply?: (filters: PoliciesTableFilters) => void;
  form: FormInstance<PoliciesTableFilters>;
}

export const PoliciesTableFiltersForm: FC<PoliciesTableFiltersFormProps> = ({ onApply, form }) => {
  const { t } = useTranslation(translationNamespace);

  const onClear = () => {
    form.resetFields();
    onApply?.(form.getFieldsValue());
  };

  return (
    <Form form={form} layout="vertical" onFinish={onApply}>
      <Row gutter={16}>
        <Col span={6}>
          <Form.Item label={t(`${Feature.SHARED}:name`)} name={PoliciesTableFilter.NAME}>
            <Select suffixIcon={null} size="large" mode="tags" allowClear open={false} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label={t(`${Feature.SHARED}:method`)} name={PoliciesTableFilter.METHOD}>
            <Select size="large" mode="multiple" allowClear>
              {Object.values(Methods).map((method, i) => (
                <Select.Option key={i} value={method}>
                  {t(`${Feature.SHARED}:methods.${method.toLocaleLowerCase()}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label={t(`${Feature.SHARED}:path`)} name={PoliciesTableFilter.PATH}>
            <Select suffixIcon={null} size="large" mode="tags" allowClear open={false} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label={t(`${Feature.SHARED}:createdAt`)} name={PoliciesTableFilter.CREATED_AT}>
            <RangePicker css={fullWidthStyles} />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item label={t(`${Feature.SHARED}:updatedAt`)} name={PoliciesTableFilter.UPDATED_AT}>
            <RangePicker css={fullWidthStyles} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col>
          <Button type="primary" htmlType="submit" size="large" icon={<CheckOutlined />}>
            {t(`${Feature.SHARED}:apply`)}
          </Button>
        </Col>
        <Col>
          <Button htmlType="button" size="large" onClick={onClear}>
            {t(`${Feature.SHARED}:clear`)}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

const fullWidthStyles = css`
  width: 100%;
  border-radius: 0px;
`;
